import { MobilityInfo } from '@beat-venues/common-types';
import {
    ClipboardIcon,
    EventPracticalTile,
    Heading,
    Logo,
    Modal,
    QrcodeIcon,
} from '@beat-venues/ui/react';
import { QRCodeSVG } from 'qrcode.react';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EventTilesProps {
    mobilityInfo: MobilityInfo[];
}

export const MobilityTile = ({
    mobilityInfo,
}: EventTilesProps): ReactElement => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState({
        nmbs: false,
        mivb: false,
    });
    const [bannerVisible, setBannerVisible] = useState(false);
    const [showQrCode, setShowQrCode] = useState(false);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(
            () => {
                setBannerVisible(true);
                setTimeout(() => {
                    setBannerVisible(false);
                }, 3000);
            },
            (err) => {
                console.error('Failed to copy text: ', err);
            }
        );
    };

    return (
        <>
            {mobilityInfo?.length > 0 &&
                mobilityInfo.some((mobility) => mobility.mivbCodeValue) && (
                    <EventPracticalTile
                        className="py-7"
                        icon={<Logo size="sm" venueId="mivb" />}
                        subTitle={t('events.MIVBSubTitle') || ''}
                        title={t('events.MIVBTitle')}
                        onClick={() =>
                            setShowModal({ nmbs: false, mivb: true })
                        }
                    />
                )}
            {mobilityInfo?.length > 0 &&
                mobilityInfo.some((mobility) => mobility.nmbsCodeValue) && (
                    <EventPracticalTile
                        className="py-7"
                        icon={<Logo size="sm" venueId="nmbs" />}
                        subTitle={t('events.MIVBSubTitle') || ''}
                        title={t('events.NMBSTitle')}
                        onClick={() =>
                            setShowModal({ nmbs: true, mivb: false })
                        }
                    />
                )}
            <Modal
                isShown={showModal.mivb || showModal.nmbs}
                onClose={() => setShowModal({ nmbs: false, mivb: false })}
            >
                <Modal.Header
                    onClose={() => setShowModal({ nmbs: false, mivb: false })}
                />
                <Modal.Content className="flex flex-col gap-8 pb-4">
                    <Heading as="h5" className="text-center">
                        {showModal.mivb && t('events.MIVBHeader')}
                        {showModal.nmbs && t('events.NMBSHeader')}
                    </Heading>
                    {bannerVisible && (
                        <div className="text-ta-brand-700 fixed left-0 right-0 top-0 bg-green-500 py-2 text-center">
                            {t('events.copyToClipboard')}
                        </div>
                    )}
                    <div className="mx-auto flex flex-col gap-4">
                        {mobilityInfo?.map((mobility, index) =>
                            mobility.mivbCodeValue && showModal.mivb ? (
                                <div>
                                    <div
                                        key={index}
                                        className="flex gap-2 text-xl"
                                    >
                                        <span className="py-1.5">
                                            MIVB{index + 1}:
                                        </span>
                                        <span
                                            className="group mb-1 flex items-center gap-2 rounded-sm bg-[#fff] p-1.5 text-xl hover:underline"
                                            style={{
                                                fontFamily:
                                                    'monospace, sans-serif',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setShowQrCode(true)}
                                        >
                                            {mobility.mivbCodeValue}
                                            <QrcodeIcon className="h-5 w-5 group-hover:scale-125" />
                                        </span>
                                    </div>
                                    <Modal
                                        className="md:max-auto w-full md:h-1/2"
                                        isShown={showQrCode}
                                        onClose={() => setShowQrCode(false)}
                                    >
                                        <Modal.Header
                                            onClose={() => setShowQrCode(false)}
                                        />
                                        <Modal.Content>
                                            <QRCodeSVG
                                                className="mx-auto"
                                                size={256}
                                                value={mobility.mivbCodeValue}
                                            />
                                        </Modal.Content>
                                    </Modal>
                                </div>
                            ) : mobility.nmbsCodeValue && showModal.nmbs ? (
                                <div key={index} className="flex gap-2 text-xl">
                                    <span className="py-1.5">
                                        NMBS{index + 1}:
                                    </span>
                                    <span
                                        className="group mb-1 flex items-center gap-2 rounded-sm bg-[#fff] p-1.5 text-xl hover:underline"
                                        style={{
                                            fontFamily: 'monospace, sans-serif',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            copyToClipboard(
                                                mobility.nmbsCodeValue
                                            )
                                        }
                                    >
                                        {mobility.nmbsCodeValue}
                                        <ClipboardIcon className="h-5 w-5 group-hover:scale-125" />
                                    </span>
                                </div>
                            ) : null
                        )}
                    </div>
                </Modal.Content>
            </Modal>
        </>
    );
};
